import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Icons } from "./icons.jsx";
import { LiveDot } from "./primitives.jsx";
import {
  HoverVideoContainer,
  HoverVideoGradientBg,
  HoverVideoGrid,
  HoverVideoGlow,
  HoverVideoShimmer,
  HoverVideoElement,
  HoverVideoOverlay,
  HoverVideoAvatarWrap,
  HoverVideoAvatarRing,
  HoverVideoAvatarImg,
  HoverVideoPlayBadge,
  HoverVideoGuruLabel,
  HoverVideoGuruSubtitle,
  HoverVideoGuruName,
  HoverVideoLoadingBadge,
  HoverVideoPlayingBadge,
} from "./style.js";

// ── HoverVideoArt — smooth PiP ────────────────────────────────────────────────
const HoverVideoArt = ({
  program,
  hovered,
  playToken,
  onAskPlay,
  fillHeight = false,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const delayTimer = useRef(null);
  const [accentStart, accentEnd] = program.accent ?? ["#01ECFF", "#962EFF"];

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (hovered && playToken === program.id) {
      delayTimer.current = setTimeout(() => {
        setShowVideo(true);
        video.play().catch(() => {});
      }, 380);
    } else {
      clearTimeout(delayTimer.current);
      setShowVideo(false);
      try {
        video.pause();
        video.currentTime = 0;
      } catch (_) {}
    }
    return () => clearTimeout(delayTimer.current);
  }, [hovered, playToken, program.id]);

  useEffect(() => {
    if (hovered) onAskPlay?.(program.id);
  }, [hovered, program.id, onAskPlay]);

  const AVATAR = 96,
    PIP_SCALE = 0.45,
    PIP_PAD = 14;
  const isPip = hovered && showVideo;
  const { width, height } = dims;
  const aLeft =
    width > 0 ? (isPip ? width - PIP_PAD - AVATAR * PIP_SCALE : width / 2 - AVATAR / 2) : 0;
  const aTop =
    height > 0 ? (isPip ? height - PIP_PAD - AVATAR * PIP_SCALE : height / 2 - AVATAR / 2) : 0;
  const aSize = isPip ? AVATAR * PIP_SCALE : AVATAR;

  return (
    <HoverVideoContainer ref={containerRef} $fillHeight={fillHeight}>
      <HoverVideoGradientBg $a={accentStart} $b={accentEnd} />
      <HoverVideoGrid />
      <HoverVideoGlow $a={accentStart} />
      {!hovered && <HoverVideoShimmer />}
      <HoverVideoElement
        ref={videoRef}
        muted
        playsInline
        loop
        preload="none"
        src={program.intro_video_url}
        $showVideo={showVideo}
      />
      <HoverVideoOverlay $showVideo={showVideo} />
      {width > 0 && (
        <HoverVideoAvatarWrap $left={aLeft} $top={aTop} $size={aSize}>
          <HoverVideoAvatarRing $a={accentStart} $b={accentEnd} $isPip={isPip} />
          <HoverVideoAvatarImg
            $src={program.guru?.photo}
            $isPip={isPip}
          />
          {!isPip && (
            <HoverVideoPlayBadge $a={accentStart} $b={accentEnd}>
              <Icons.play size={14} style={{ color: "#fff" }} />
            </HoverVideoPlayBadge>
          )}
        </HoverVideoAvatarWrap>
      )}
      <HoverVideoGuruLabel $hovered={hovered}>
        <HoverVideoGuruSubtitle>Your Guru</HoverVideoGuruSubtitle>
        <HoverVideoGuruName>
          {program.guru?.full_name?.split(" ")[0]}
        </HoverVideoGuruName>
      </HoverVideoGuruLabel>
      {!showVideo && hovered && (
        <HoverVideoLoadingBadge>
          <Icons.play size={12} /> <span>Loading intro…</span>
        </HoverVideoLoadingBadge>
      )}
      {showVideo && (
        <HoverVideoPlayingBadge>
          <LiveDot />{" "}
          <span>{program.guru?.full_name?.split(" ")[0]}'s 30-sec intro</span>
        </HoverVideoPlayingBadge>
      )}
    </HoverVideoContainer>
  );
};

export default HoverVideoArt;
